import type { TChatAutoTool } from '@/apiClient/queries/chatQueryTypes'
import type { IUploadedFile } from '@/apiClient/types/file'
import {
  isPosthogFlagOn,
  PosthogFeatureFlags,
  type PosthogFeatureFlagKey,
} from '@/lib/posthog'
import type { EventTypesFileGeneration } from '@/store/socketEvents/types'
import type { IIcons } from '@/views/components/ui/icons/types'

import type { IIcons as IUiLibIcons } from '@foundever/ui-lib'

/**
 * Files the chat can generate as a background job. Every kind runs the same pipeline:
 * the composer mode sets a `generate_*` request flag, `message_complete` names the job
 * in `background_job_type`, a placeholder file stands on the assistant row while it
 * runs, websocket progress events (where the job sends any) stream steps into
 * `ui_hints`, and the succeeded event swaps the placeholder for the real file.
 *
 * Values are the backend's job types (`background_job_type`, `pending_jobs[].job_type`).
 */
export type FileGenerationJobType = 'pptx' | 'xlsx' | 'pdf'

export type FileGenerationRequestField =
  'generate_pptx' | 'generate_xlsx' | 'generate_pdf'

interface IFileGenerationJobDefinition {
  /** Row in the composer's "+" › Create submenu. */
  optionLabel: string
  /** Icon on that row (ui-lib icon set). */
  optionIcon: IUiLibIcons
  /** Chip shown in the composer while the mode is selected. */
  toggleLabel: string
  /** Icon on the chip. */
  icon: IIcons
  /** Kill switch for starting a job and for showing a running job's placeholder. */
  flag: PosthogFeatureFlagKey
  /** Boolean on the create-chat / send-message body that asks for this job. */
  requestField: FileGenerationRequestField
  placeholderFileName: string
  /** MIME type of the generated file, also used for the placeholder row. */
  contentType: string
  /** Absent for jobs that report no progress steps (PDF). */
  progressEvent?: EventTypesFileGeneration
  /** The auto tool that starts this job; its steps row carries the job's progress. */
  toolName: TChatAutoTool
  /** `ui_hints.processing_steps[].step` the job's progress (or "Queued…") is saved under. */
  progressStep: string
  /** Title of the progress row once running or queued, before the job reports its own. */
  progressTitle: string
  /** What the tool's row reads once the file is delivered, before `: <title>`. */
  deliveredLabel: string
  succeededEvent: EventTypesFileGeneration
  failedEvent: EventTypesFileGeneration
  /** PostHog event captured when the generated file lands. */
  generatedMetricEvent: string
  failureMessage: string
  /** Prefix for console logs. */
  logLabel: string
}

/** Everything that differs between file kinds, so adding one is a data change. */
export const FILE_GENERATION_JOBS: Record<
  FileGenerationJobType,
  IFileGenerationJobDefinition
> = {
  pptx: {
    optionLabel: 'Presentation',
    optionIcon: 'filePowerpointRegular',
    toggleLabel: 'PowerPoint',
    icon: 'filePowerpoint',
    flag: PosthogFeatureFlags.PptsGeneration,
    requestField: 'generate_pptx',
    placeholderFileName: 'presentation.pptx',
    contentType:
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    progressEvent: 'pptx_job_progress',
    toolName: 'generate_powerpoint',
    progressStep: 'pptx_generation',
    progressTitle: 'Generating the presentation',
    deliveredLabel: 'Created presentation',
    succeededEvent: 'pptx_job_succeeded',
    failedEvent: 'pptx_job_failed',
    generatedMetricEvent: 'ppt_generated',
    failureMessage: 'Presentation generation failed',
    logLabel: 'PPTX',
  },
  xlsx: {
    optionLabel: 'Spreadsheet',
    optionIcon: 'fileSpreadsheetRegular',
    toggleLabel: 'Excel',
    icon: 'fileExcel',
    flag: PosthogFeatureFlags.XlsxGeneration,
    requestField: 'generate_xlsx',
    placeholderFileName: 'spreadsheet.xlsx',
    contentType:
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    progressEvent: 'xlsx_job_progress',
    toolName: 'generate_spreadsheet',
    progressStep: 'xlsx_generation',
    progressTitle: 'Generating the spreadsheet',
    deliveredLabel: 'Created spreadsheet',
    succeededEvent: 'xlsx_job_succeeded',
    failedEvent: 'xlsx_job_failed',
    generatedMetricEvent: 'xlsx_generated',
    failureMessage: 'Spreadsheet generation failed',
    logLabel: 'XLSX',
  },
  pdf: {
    optionLabel: 'PDF',
    optionIcon: 'filePdfRegular',
    toggleLabel: 'PDF',
    icon: 'filePdf',
    flag: PosthogFeatureFlags.PdfGeneration,
    requestField: 'generate_pdf',
    placeholderFileName: 'document.pdf',
    contentType: 'application/pdf',
    // The PDF job streams no steps of its own: only the "Queued…" step saved with the reply.
    toolName: 'generate_pdf',
    progressStep: 'pdf_generation',
    progressTitle: 'Generating the PDF',
    deliveredLabel: 'Created PDF',
    succeededEvent: 'pdf_generation_job_succeeded',
    failedEvent: 'pdf_generation_job_failed',
    generatedMetricEvent: 'pdf_generated',
    failureMessage: 'PDF generation failed',
    logLabel: 'PDF',
  },
}

/** Table order is menu order. */
export const FILE_GENERATION_JOB_TYPES = Object.keys(
  FILE_GENERATION_JOBS,
) as FileGenerationJobType[]

export function isFileGenerationJobType(
  value: unknown,
): value is FileGenerationJobType {
  return (
    typeof value === 'string' &&
    (FILE_GENERATION_JOB_TYPES as string[]).includes(value)
  )
}

export function isFileGenerationJobEnabled(
  jobType: FileGenerationJobType,
): boolean {
  return isPosthogFlagOn(FILE_GENERATION_JOBS[jobType].flag)
}

/** The request flag for the selected mode — nothing when no mode is selected or its flag is off. */
export function fileGenerationRequestFields(
  jobType: FileGenerationJobType | null | undefined,
): Partial<Record<FileGenerationRequestField, true>> {
  if (!jobType || !isFileGenerationJobEnabled(jobType)) return {}
  return { [FILE_GENERATION_JOBS[jobType].requestField]: true }
}

export type FileGenerationJobPhase = 'progress' | 'succeeded' | 'failed'

/** Built once from the table, so an incoming event is matched exactly once. */
const JOB_EVENT_LOOKUP = new Map<
  string,
  { jobType: FileGenerationJobType; phase: FileGenerationJobPhase }
>(
  FILE_GENERATION_JOB_TYPES.flatMap((jobType) => {
    const { progressEvent, succeededEvent, failedEvent } =
      FILE_GENERATION_JOBS[jobType]
    return (
      [
        [progressEvent, 'progress'],
        [succeededEvent, 'succeeded'],
        [failedEvent, 'failed'],
      ] as const
    )
      .filter(([event]) => !!event)
      .map(([event, phase]) => [event as string, { jobType, phase }] as const)
  }),
)

/** Which job a websocket notification type belongs to, and which phase it reports. */
export function fileGenerationJobEvent(eventType: string) {
  return JOB_EVENT_LOOKUP.get(eventType)
}

const placeholderFileIdPrefix = (jobType: FileGenerationJobType) =>
  `${jobType}-pending:`

/**
 * Stand-in file row (status `processing`) for a running job. UploadedFile renders it
 * through the normal files path, which gives the loading spinner. The id is keyed by
 * message so the store's live placeholder and ChatBubble's reload one dedupe.
 */
export function buildFileGenerationPlaceholderFile(
  jobType: FileGenerationJobType,
  messageKey: string,
): IUploadedFile {
  const { placeholderFileName, contentType } = FILE_GENERATION_JOBS[jobType]
  return {
    id: `${placeholderFileIdPrefix(jobType)}${messageKey}`,
    name: placeholderFileName,
    content_type: contentType,
    size: 0,
    status: 'processing',
  }
}

export function isFileGenerationPlaceholderFile(
  file: Pick<IUploadedFile, 'id'>,
  jobType: FileGenerationJobType,
): boolean {
  return file.id.startsWith(placeholderFileIdPrefix(jobType))
}

const isAnyFileGenerationPlaceholderFile = (file: Pick<IUploadedFile, 'id'>) =>
  FILE_GENERATION_JOB_TYPES.some((jobType) =>
    isFileGenerationPlaceholderFile(file, jobType),
  )

/**
 * A reply's files once a background job delivers one, in the order a reload lists them:
 * delivered files as they landed, then the placeholders of the jobs still running. The
 * delivering job's own placeholder (`jobType`) goes. A reply can run several jobs, so
 * everything else on it stays.
 */
export function withDeliveredJobFile(
  files: IUploadedFile[] | undefined,
  file: IUploadedFile,
  jobType?: FileGenerationJobType,
): IUploadedFile[] {
  const kept = (files ?? []).filter(
    (existing) =>
      existing.id !== file.id &&
      !(jobType && isFileGenerationPlaceholderFile(existing, jobType)),
  )
  return [
    ...kept.filter((existing) => !isAnyFileGenerationPlaceholderFile(existing)),
    file,
    ...kept.filter(isAnyFileGenerationPlaceholderFile),
  ]
}
